'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function AgeCalculator() {
  const t = useTranslations("tools.ageCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [birthDate, setBirthDate] = useState({
    month: '',
    day: '',
    year: ''
  });

  const [targetDate, setTargetDate] = useState({
    month: '',
    day: '',
    year: ''
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    const today = new Date();
    setTargetDate({
      month: String(today.getMonth() + 1),
      day: String(today.getDate()),
      year: String(today.getFullYear())
    });
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const isLeapYear = (year: number): boolean => {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  };

  const getDaysInMonth = (month: number, year: number): number => {
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (month === 2 && isLeapYear(year)) {
      return 29;
    }
    return daysInMonth[month - 1];
  };

  const calculateAge = (birth: Date, target: Date) => {
    let years = target.getFullYear() - birth.getFullYear();
    let months = target.getMonth() - birth.getMonth();
    let days = target.getDate() - birth.getDate();

    if (days < 0) {
      months--;
      const prevMonth = target.getMonth() === 0 ? 12 : target.getMonth();
      const prevYear = target.getMonth() === 0 ? target.getFullYear() - 1 : target.getFullYear();
      days += getDaysInMonth(prevMonth, prevYear);
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    return { years, months, days };
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-US');
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const birthMonth = parseInt(birthDate.month);
    const birthDay = parseInt(birthDate.day);
    const birthYear = parseInt(birthDate.year);

    const targetMonth = parseInt(targetDate.month);
    const targetDay = parseInt(targetDate.day);
    const targetYear = parseInt(targetDate.year);

    if (!birthMonth || !birthDay || !birthYear || !targetMonth || !targetDay || !targetYear) {
      setError(t('errorAllFields'));
      return;
    }

    if (birthDay > getDaysInMonth(birthMonth, birthYear)) {
      setError(t('errorInvalidBirthDate'));
      return;
    }

    if (targetDay > getDaysInMonth(targetMonth, targetYear)) {
      setError(t('errorInvalidTargetDate'));
      return;
    }

    const birth = new Date(birthYear, birthMonth - 1, birthDay);
    const target = new Date(targetYear, targetMonth - 1, targetDay);

    if (target < birth) {
      setError(t('errorTargetBeforeBirth'));
      return;
    }

    const age = calculateAge(birth, target);

    const totalDays = Math.floor((target.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24));
    const totalMonths = age.years * 12 + age.months;
    const totalWeeks = Math.floor(totalDays / 7);
    const totalHours = totalDays * 24;
    const totalMinutes = totalHours * 60;
    const totalSeconds = totalMinutes * 60;

    let nextBirthday = new Date(target.getFullYear(), birthMonth - 1, birthDay);
    if (nextBirthday <= target) {
      nextBirthday = new Date(target.getFullYear() + 1, birthMonth - 1, birthDay);
    }

    const daysUntilBirthday = Math.floor((nextBirthday.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

    setResults({
      age,
      totalMonths,
      totalWeeks,
      totalDays,
      totalHours,
      totalMinutes,
      totalSeconds,
      nextBirthday,
      daysUntilBirthday
    });
  };

  const monthNames = [
    t('monthJanuary'), t('monthFebruary'), t('monthMarch'), t('monthApril'),
    t('monthMay'), t('monthJune'), t('monthJuly'), t('monthAugust'),
    t('monthSeptember'), t('monthOctober'), t('monthNovember'), t('monthDecember')
  ];

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', textAlign: 'center' as const },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.875rem' },
    { fontSize: '0.9375rem' },
    { fontSize: '1rem' }
  );

  const dateInputWrapperStyle = getResponsiveStyle(
    { display: 'grid', gap: '0.75rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(3, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1.0625rem' },
    { padding: '1rem', fontSize: '1.125rem' }
  );

  const errorMessageStyle: React.CSSProperties = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    border: '2px solid #fca5a5',
    display: error ? 'block' : 'none'
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #2563eb'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: colors.textPrimary, textAlign: 'center' as const },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const ageDisplayStyle = getResponsiveStyle(
    { background: 'white', borderRadius: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)', marginBottom: '1.5rem' },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const primaryAgeStyle = getResponsiveStyle(
    { textAlign: 'center' as const, fontWeight: 700, color: '#2563eb', marginBottom: '0.5rem' },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const ageLabelStyle = getResponsiveStyle(
    { textAlign: 'center' as const, color: '#64748b', marginBottom: '1.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const detailedAgeStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginTop: '1.5rem' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }
  );

  const ageUnitStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    padding: '1rem',
    borderRadius: '0.75rem',
    textAlign: 'center',
    border: '2px solid #bfdbfe'
  };

  const ageUnitValueStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#2563eb', marginBottom: '0.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const ageUnitLabelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  };

  const nextBirthdayStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.75rem',
      border: '2px solid #fbbf24',
      marginTop: '1.5rem'
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const nextBirthdayTitleStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#92400e',
    marginBottom: '0.5rem',
    fontSize: '1rem'
  };

  const nextBirthdayInfoStyle = getResponsiveStyle(
    { color: '#78350f' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle: React.CSSProperties = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.9375rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' }
  );

  const refLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🎂 {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>{t('birthDateLabel')}</label>
              <div style={dateInputWrapperStyle}>
                <select
                  style={inputStyle}
                  value={birthDate.month}
                  onChange={(e) => setBirthDate({...birthDate, month: e.target.value})}
                  required
                >
                  <option value="">{t('monthPlaceholder')}</option>
                  {monthNames.map((month, index) => (
                    <option key={index} value={String(index + 1)}>{month}</option>
                  ))}
                </select>
                <input
                  type="number"
                  style={inputStyle}
                  placeholder={t('dayPlaceholder')}
                  min="1"
                  max="31"
                  value={birthDate.day}
                  onChange={(e) => setBirthDate({...birthDate, day: e.target.value})}
                  required
                />
                <input
                  type="number"
                  style={inputStyle}
                  placeholder={t('yearPlaceholder')}
                  min="1900"
                  max="2100"
                  value={birthDate.year}
                  onChange={(e) => setBirthDate({...birthDate, year: e.target.value})}
                  required
                />
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t('targetDateLabel')}</label>
              <div style={dateInputWrapperStyle}>
                <select
                  style={inputStyle}
                  value={targetDate.month}
                  onChange={(e) => setTargetDate({...targetDate, month: e.target.value})}
                  required
                >
                  <option value="">{t('monthPlaceholder')}</option>
                  {monthNames.map((month, index) => (
                    <option key={index} value={String(index + 1)}>{month}</option>
                  ))}
                </select>
                <input
                  type="number"
                  style={inputStyle}
                  placeholder={t('dayPlaceholder')}
                  min="1"
                  max="31"
                  value={targetDate.day}
                  onChange={(e) => setTargetDate({...targetDate, day: e.target.value})}
                  required
                />
                <input
                  type="number"
                  style={inputStyle}
                  placeholder={t('yearPlaceholder')}
                  min="1900"
                  max="2100"
                  value={targetDate.year}
                  onChange={(e) => setTargetDate({...targetDate, year: e.target.value})}
                  required
                />
              </div>
            </div>

            <div style={errorMessageStyle}>{error}</div>

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 20px rgba(37, 99, 235, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>{t('resultsTitle')}</h3>

              <div style={ageDisplayStyle}>
                <div style={primaryAgeStyle}>
                  {results.age.years} {t('yearsLabel')}
                </div>
                <div style={ageLabelStyle}>
                  {results.age.months} {t('monthsLabel')} {t('andLabel')} {results.age.days} {t('daysLabel')}
                </div>

                <div style={detailedAgeStyle}>
                  <div style={ageUnitStyle}>
                    <span style={ageUnitValueStyle}>{formatNumber(results.totalMonths)}</span>
                    <span style={ageUnitLabelStyle}>{t('totalMonths')}</span>
                  </div>
                  <div style={ageUnitStyle}>
                    <span style={ageUnitValueStyle}>{formatNumber(results.totalWeeks)}</span>
                    <span style={ageUnitLabelStyle}>{t('totalWeeks')}</span>
                  </div>
                  <div style={ageUnitStyle}>
                    <span style={ageUnitValueStyle}>{formatNumber(results.totalDays)}</span>
                    <span style={ageUnitLabelStyle}>{t('totalDays')}</span>
                  </div>
                  <div style={ageUnitStyle}>
                    <span style={ageUnitValueStyle}>{formatNumber(results.totalHours)}</span>
                    <span style={ageUnitLabelStyle}>{t('totalHours')}</span>
                  </div>
                  <div style={ageUnitStyle}>
                    <span style={ageUnitValueStyle}>{formatNumber(results.totalMinutes)}</span>
                    <span style={ageUnitLabelStyle}>{t('totalMinutes')}</span>
                  </div>
                  <div style={ageUnitStyle}>
                    <span style={ageUnitValueStyle}>{formatNumber(results.totalSeconds)}</span>
                    <span style={ageUnitLabelStyle}>{t('totalSeconds')}</span>
                  </div>
                </div>
              </div>

              <div style={nextBirthdayStyle}>
                <div style={nextBirthdayTitleStyle}>{t('nextBirthdayTitle')}</div>
                <div style={nextBirthdayInfoStyle}>
                  {monthNames[results.nextBirthday.getMonth()]} {results.nextBirthday.getDate()}, {results.nextBirthday.getFullYear()} ({t('inLabel')} {results.daysUntilBirthday} {t('daysLabel')})
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep1')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep2')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep3')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep4')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.375rem' : '1.5rem', marginTop: '1.5rem'}}>{t('detailsUseCasesTitle')}</h3>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsUseCase1')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsUseCase2')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsUseCase3')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsUseCase4')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsUseCase5')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsUseCase6')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.375rem' : '1.5rem', marginTop: '1.5rem'}}>{t('detailsFeaturesTitle')}</h3>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsFeature1')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsFeature2')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsFeature3')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsFeature4')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsFeature5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faqQuestion1')}</div>
          <div style={faqAnswerStyle}>{t('faqAnswer1')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faqQuestion2')}</div>
          <div style={faqAnswerStyle}>{t('faqAnswer2')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faqQuestion3')}</div>
          <div style={faqAnswerStyle}>{t('faqAnswer3')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faqQuestion4')}</div>
          <div style={faqAnswerStyle}>{t('faqAnswer4')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faqQuestion5')}</div>
          <div style={faqAnswerStyle}>{t('faqAnswer5')}</div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://brightseotools.com/age-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference1')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://www.graphcalc.com/age-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference2')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://agecalculator.com.pk/benefits-of-using-an-age-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference3')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="http://www.businesstipsfor90smentor.rf.gd/blog/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference4')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}